import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ArrowLeft, Mail, Globe, MapPin, Phone, FileCheck2, AlertTriangle, Building2,
} from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { VendorLogo, Badges, copyEmail } from "@/pages/Marketplace";
import { InviteToRfqDialog } from "@/components/InviteToRfqDialog";
import { VisitorRfqDialog } from "@/components/VisitorRfqDialog";
import { useAuth } from "@/context/AuthContext";
import { PublicFooter } from "@/components/PublicShell";

const pub = axios.create({ baseURL: API });

const statusPill = {
  valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200" },
  expiring: { label: "Expiring soon", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100" },
  expired: { label: "Expired", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200" },
  unknown: { label: "No expiry read", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

export default function VendorPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [missing, setMissing] = useState(false);
  const [dialog, setDialog] = useState("");

  const sendRfq = () => {
    if (user?.account_type === "contractor") return setDialog("invite");
    if (!user) return setDialog("visitor");
    copyEmail(v.contact_email);
  };

  useEffect(() => {
    pub.get(`/public/vendors/${id}`).then((r) => setV(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">This vendor is not listed.</p>
        <Button onClick={() => navigate("/")}>Back to marketplace</Button>
      </div>
    );
  }
  if (!v) return <div className="min-h-screen flex justify-center pt-40"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="back-to-marketplace" className="inline-flex items-center gap-3 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-7 w-auto" />
          </Link>
          <Button data-testid="vendor-profile-send-rfq" onClick={sendRfq} className="h-10 gap-2 font-semibold bg-brand text-brand-foreground hover:bg-brand/90">
            <Mail className="w-4 h-4" /> Send RFQ
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <VendorLogo vendor={v} size={96} />
          <div className="flex-1">
            <h1 data-testid="vendor-company-name" className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
              {v.company_name}
            </h1>
            <div className="text-sm text-muted-foreground mt-2 flex flex-wrap items-center gap-4">
              <span className="px-2 py-0.5 rounded-md bg-secondary text-[11px] font-bold uppercase tracking-wide">{v.vendor_type}</span>
              {(v.city || v.country) && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[v.city, v.country].filter(Boolean).join(", ")}</span>}
              {v.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{v.phone}</span>}
              {v.website && (
                <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary font-semibold">
                  <Globe className="w-3.5 h-3.5" />{v.website}
                </a>
              )}
              {v.year_established && <span>Established {v.year_established}</span>}
            </div>
            <div className="mt-3"><Badges v={v} /></div>
          </div>
        </div>

        {v.description && (
          <p className="text-sm text-muted-foreground mt-8 max-w-3xl leading-relaxed">{v.description}</p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-4">Trades & scope</div>
              {v.business_types?.length ? (
                <div className="flex flex-wrap gap-2">
                  {v.business_types.map((t) => (
                    <span key={t} className="px-3 py-1 rounded-full bg-secondary text-xs font-semibold">{t}</span>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Not published yet.</p>}
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-4">Projects</div>
              {v.projects?.length ? (
                <div className="divide-y divide-border">
                  {v.projects.map((p, i) => (
                    <div key={i} data-testid={`vendor-project-${i}`} className="py-3 flex items-start justify-between gap-4 first:pt-0 last:pb-0">
                      <div>
                        <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{[p.client, p.year].filter(Boolean).join(" · ")}</div>
                      </div>
                      {p.value && <div className="text-xs font-mono text-muted-foreground shrink-0">{p.value}</div>}
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No projects published yet.</p>}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-4">Compliance documents</div>
              {v.documents?.length ? (
                <div className="space-y-2">
                  {v.documents.map((d, i) => {
                    const sp = statusPill[d.status] || statusPill.unknown;
                    return (
                      <div key={i} data-testid={`vendor-doc-${i}`} className="flex items-center justify-between gap-2 text-xs">
                        <span className="inline-flex items-center gap-1.5 font-medium">
                          {d.status === "expired" ? <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> : <FileCheck2 className="w-3.5 h-3.5 text-muted-foreground" />}
                          {d.doc_type}
                        </span>
                        <span className={`px-2 py-0.5 rounded-md font-semibold ${sp.cls}`}>{sp.label}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>}
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-3">Registration</div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Trade licence</span><span className="font-mono">{v.trade_license_no || "—"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">VAT / TRN</span><span className="font-mono">{v.vat_number || "—"}</span></div>
              </div>
              {v.address && (
                <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground whitespace-pre-line flex gap-2">
                  <Building2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />{v.address}
                </div>
              )}
            </div>

            <Button data-testid="vendor-profile-send-rfq-2" onClick={sendRfq} className="w-full h-11 gap-2 font-semibold bg-brand text-brand-foreground hover:bg-brand/90">
              <Mail className="w-4 h-4" /> Send RFQ
            </Button>
          </div>
        </div>
      </div>
      {dialog === "invite" && <InviteToRfqDialog vendor={{ ...v, id }} onClose={() => setDialog("")} />}
      {dialog === "visitor" && <VisitorRfqDialog vendor={v} onClose={() => setDialog("")} />}
      <PublicFooter />
    </div>
  );
}
